import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getKeycloak, withVerifiedToken } from '../auth/keycloak';
import { createGraphQLClient } from '../lib/graphql-client';
import { BROKERS_QUERY } from '../lib/queries';
import type { Broker, ProductKey } from '../types/vehicle';

interface BrokersGraphQLResponse {
  brokers: Array<{
    name: string;
    products: string[];
    includedDealerLocationIds: number[] | null;
    excludedDealerLocationIds: number[] | null;
  }>;
}

const VALID_PRODUCTS: ReadonlySet<string> = new Set(['PL', 'FL', 'HK', 'OL']);

async function fetchBrokers(): Promise<Broker[]> {
  return withVerifiedToken(getKeycloak(), async (verifiedToken) => {
    const client = createGraphQLClient(verifiedToken);
    const data = await client.request<BrokersGraphQLResponse>(BROKERS_QUERY);

    return data.brokers.map((b) => ({
      name: b.name,
      products: b.products.filter((p): p is ProductKey => VALID_PRODUCTS.has(p)),
      includedDealerLocationIds: b.includedDealerLocationIds ?? null,
      excludedDealerLocationIds: b.excludedDealerLocationIds ?? null
    }));
  });
}

export const BROKERS_QUERY_KEY = 'brokers';

export function useBrokers(): UseQueryResult<Broker[], Error> {
  return useQuery({
    queryKey: [BROKERS_QUERY_KEY],
    queryFn: () => fetchBrokers(),
    staleTime: 10 * 60 * 1000
  });
}

// Pure helper — used by the modal to derive (broker × product) cells for a vehicle.
export function getApplicableBrokerProducts(
  brokers: Broker[],
  dealerLocationId: number | null
): Array<{ broker: string; product: ProductKey }> {
  return brokers.flatMap((broker) => {
    if (!brokerCoversLocation(broker, dealerLocationId)) {
      return [];
    }

    return broker.products.map((product) => ({ broker: broker.name, product }));
  });
}

function brokerCoversLocation(broker: Broker, dealerLocationId: number | null): boolean {
  const included = broker.includedDealerLocationIds ?? [];
  const excluded = broker.excludedDealerLocationIds ?? [];

  if (included.length > 0) {
    return dealerLocationId !== null && included.includes(dealerLocationId);
  }

  if (excluded.length > 0) {
    return dealerLocationId === null ? true : !excluded.includes(dealerLocationId);
  }

  // No filters configured = broker covers all dealers
  return true;
}
